import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { breakpoints, spacing, touchTargets, gridLayouts } from '../components/responsive/responsiveConfig';

/**
 * Custom hook for responsive design utilities
 * 
 * This hook provides easy access to responsive breakpoints, spacing, and layout utilities
 * for consistent responsive design across the application.
 * 
 * @returns An object with responsive design utilities and helpers
 */
const useResponsiveDesign = () => {
  const theme = useTheme();
  
  // Responsive breakpoint flags
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  // Touch device detection (approximation based on screen size)
  const isTouchDevice = useMediaQuery('(pointer: coarse)') || isMobile || isTablet;
  
  // Screen size utilities
  const screenSize = {
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
  };
  
  // Helper function for responsive spacing
  const getSpacing = (size: keyof typeof spacing) => theme.spacing(spacing[size]);
  
  // Helper function for responsive padding
  const getResponsivePadding = () => ({
    p: {
      xs: getSpacing('xs'),
      sm: getSpacing('sm'),
      md: getSpacing('md'),
      lg: getSpacing('lg')
    }
  });
  
  // Helper function for responsive margins
  const getResponsiveMargin = () => ({
    m: {
      xs: getSpacing('xs'),
      sm: getSpacing('sm'),
      md: getSpacing('md'),
      lg: getSpacing('lg')
    }
  });
  
  // Helper for bottom navigation adjustment
  const bottomNavAdjustment = {
    pb: isMobile ? `${theme.spacing(8)}` : undefined,
  };
  
  // Touch target sizing based on device
  const getTouchTargetSize = () => {
    if (isMobile) return touchTargets.mobile;
    if (isTablet) return touchTargets.tablet;
    return touchTargets.desktop;
  };
  
  // Grid layouts for responsive grids
  const getGridConfig = () => gridLayouts;
  
  return {
    // Breakpoint flags
    isMobile,
    isTablet,
    isDesktop,
    isLargeDesktop,
    
    // Screen details
    screenSize,
    isTouchDevice,
    
    // Spacing helpers
    getSpacing,
    getResponsivePadding,
    getResponsiveMargin,
    bottomNavAdjustment,
    
    // Touch target sizing
    getTouchTargetSize,
    
    // Grid configuration
    gridLayouts: getGridConfig(),
    
    // Original breakpoints
    breakpoints,
  };
};

export default useResponsiveDesign;
